import { useState, useCallback, useEffect } from 'react';
import {
  getAutoMarkedReviews,
  submitModerationFeedback,
  getModerationAnalytics,
} from '../../../api/movieService';
import moderationCacheService from '../../../services/moderationCacheService';
import {
  BoltIcon,
  FunnelIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  UserIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const AutoMarkedReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [filters, setFilters] = useState({
    reviewedStatus: 'pending',
    confidenceMin: 0.8,
    confidenceMax: 1.0,
    dateFrom: '',
    dateTo: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 20,
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Optimized fetch auto-marked reviews with caching
  const fetchAutoMarkedReviews = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        // Use cache service for auto-marked reviews API
        const response = await moderationCacheService.cachedApiCall(
          'auto_marked_reviews',
          async () => await getAutoMarkedReviews(page, pagination.pageSize, filters),
          { page, pageSize: pagination.pageSize, ...filters }
        );

        // Debug log
        console.log('✅ AutoMarkedReviews API response:', {
          count: response?.data?.length || response?.length || 0,
          fromCache: response.__fromCache || false,
        });

        const reviewList = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setReviews(reviewList);
        setPagination({
          currentPage: response.current_page || 1,
          totalPages: response.total_pages || 1,
          totalCount: response.count || reviewList.length,
          pageSize: response.page_size || 20,
        });
      } catch (error) {
        console.error('Error fetching auto-marked reviews:', error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.pageSize]
  );

  // Optimized fetch analytics with caching
  const fetchAnalytics = useCallback(async () => {
    try {
      // Use cache service for moderation analytics API
      const response = await moderationCacheService.cachedApiCall(
        'moderation_analytics',
        async () => await getModerationAnalytics(30),
        { days: 30 }
      );

      setAnalytics(response.data);

      console.log('✅ Moderation analytics loaded:', {
        totalFeedback: response.data?.summary?.total_feedback || 0,
        fromCache: response.__fromCache || false,
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAutoMarkedReviews();
    fetchAnalytics();
  }, [fetchAutoMarkedReviews, fetchAnalytics]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    moderationCacheService.invalidateCache('auto_marked_reviews');
    fetchAutoMarkedReviews(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      reviewedStatus: 'pending',
      confidenceMin: 0.8,
      confidenceMax: 1.0,
      dateFrom: '',
      dateTo: '',
    });
  };

  // Handle pagination
  const handlePageChange = page => {
    fetchAutoMarkedReviews(page);
  };

  // Submit feedback
  const handleSubmitFeedback = async (reviewId, feedbackData) => {
    try {
      await submitModerationFeedback(reviewId, feedbackData);
      // Invalidate cache and refresh the reviews list
      moderationCacheService.invalidateCache('auto_marked_reviews');
      moderationCacheService.invalidateCache('moderation_analytics');
      fetchAutoMarkedReviews(pagination.currentPage);
      setShowFeedbackModal(false);
      setSelectedReview(null);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  // Get confidence color
  const getConfidenceColor = confidence => {
    if (confidence >= 0.9) return 'text-red-600 bg-red-100';
    if (confidence >= 0.8) return 'text-orange-600 bg-orange-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  // Get status color
  const getStatusColor = hasReviewed => {
    return hasReviewed ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100';
  };

  // Format date
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-yellow-100 p-2">
              <BoltIcon className="size-6 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Auto-marked Reviews</h1>
              <p className="text-gray-600">
                Quản lý và đánh giá các reviews được đánh dấu tự động bởi AI
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              moderationCacheService.invalidateCache('auto_marked_reviews');
              moderationCacheService.invalidateCache('moderation_analytics');
              fetchAutoMarkedReviews(pagination.currentPage);
            }}
            className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            <ArrowPathIcon className="size-4" />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-blue-100 p-2">
                <ChartBarIcon className="size-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Độ chính xác tổng</p>
                <p className="text-lg font-semibold">
                  {(analytics.summary?.overall_accuracy * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-green-100 p-2">
                <CheckCircleIcon className="size-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Tổng feedback</p>
                <p className="text-lg font-semibold">{analytics.summary?.total_feedback}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-yellow-100 p-2">
                <BoltIcon className="size-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Auto-marked (30d)</p>
                <p className="text-lg font-semibold">
                  {analytics.volume_metrics?.auto_marked_reviews}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-purple-100 p-2">
                <ClockIcon className="size-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Chờ xử lý</p>
                <p className="text-lg font-semibold">{pagination.totalCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center space-x-3">
          <FunnelIcon className="size-5 text-gray-600" />
          <h3 className="text-lg font-medium text-black">Bộ lọc</h3>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-900">Trạng thái</label>
            <select
              value={filters.reviewedStatus}
              onChange={e => handleFilterChange('reviewedStatus', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:ring-blue-500"
            >
              <option className="text-black" value="pending">
                Chờ xử lý
              </option>
              <option className="text-black" value="reviewed">
                Đã xử lý
              </option>
              <option className="text-black" value="all">
                Tất cả
              </option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm  font-medium text-gray-700">
              Confidence tối thiểu
            </label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={filters.confidenceMin}
              onChange={e => handleFilterChange('confidenceMin', parseFloat(e.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Confidence tối đa
            </label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={filters.confidenceMax}
              onChange={e => handleFilterChange('confidenceMax', parseFloat(e.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Từ ngày</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={e => handleFilterChange('dateFrom', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Đến ngày</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={e => handleFilterChange('dateTo', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black  focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-4 flex space-x-3">
          <button
            onClick={applyFilters}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Áp dụng bộ lọc
          </button>
          <button
            onClick={resetFilters}
            className="rounded-lg bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700"
          >
            Đặt lại
          </button>
        </div>
      </div>

      {/* Reviews List */}
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="border-b p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-black">
              {`Danh sách Reviews (${
                typeof pagination.totalCount === 'number'
                  ? pagination.totalCount
                  : Array.isArray(reviews)
                    ? reviews.length
                    : 0
              })`}
            </h3>
            <div className="text-sm text-gray-600">
              Trang {pagination.currentPage} / {pagination.totalPages}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Đang tải...</p>
          </div>
        ) : Array.isArray(reviews) && reviews.length === 0 ? (
          <div className="p-12 text-center">
            <BoltIcon className="mx-auto mb-4 size-12 text-gray-400" />
            <p className="text-gray-600">Không có reviews nào được tìm thấy</p>
          </div>
        ) : (
          <div className="divide-y">
            {Array.isArray(reviews) &&
              reviews.map(review => {
                const moderationFeedback = Array.isArray(review.moderation_feedback)
                  ? review.moderation_feedback
                  : [];
                return (
                  <div key={review.id} className="p-6 transition-colors hover:bg-gray-50">
                    <div className="flex items-start space-x-4">
                      {/* Review Content */}
                      <div className="flex-1">
                        <div className="mb-2 flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <UserIcon className="size-4 text-gray-500" />
                            <span className="font-medium text-gray-900">
                              {review.user?.username || 'Anonymous'}
                            </span>
                          </div>

                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${getConfidenceColor(
                              review.spoiler_confidence
                            )}`}
                          >
                            Confidence: {(review.spoiler_confidence * 100).toFixed(1)}%
                          </span>

                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(
                              moderationFeedback.length > 0
                            )}`}
                          >
                            {moderationFeedback.length > 0 ? 'Đã xử lý' : 'Chờ xử lý'}
                          </span>

                          <div className="flex items-center space-x-1 text-sm text-gray-500">
                            <CalendarDaysIcon className="size-4" />
                            <span>{formatDate(review.created_at)}</span>
                          </div>
                        </div>

                        {/* Enhanced Processed Review Information */}
                        {moderationFeedback.length > 0 && (
                          <div className="mb-3 rounded-r-lg border-l-4 border-green-400 bg-green-50 p-3">
                            <div className="mb-2 flex items-center space-x-2">
                              <CheckCircleIcon className="size-4 text-green-600" />
                              <span className="text-sm font-medium text-green-800">
                                Đã được xử lý bởi Moderator
                              </span>
                            </div>
                            {moderationFeedback.map((feedback, index) => (
                              <div key={index} className="text-sm text-green-700">
                                <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                                  <div>
                                    <span className="font-medium">Quyết định:</span>{' '}
                                    <span
                                      className={`rounded-full px-2 py-1 text-xs ${
                                        feedback.moderator_decision === 'approve_as_spoiler'
                                          ? 'bg-red-100 text-red-700'
                                          : feedback.moderator_decision === 'approve_as_non_spoiler'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {feedback.moderator_decision === 'approve_as_spoiler'
                                        ? 'Phê duyệt là Spoiler'
                                        : feedback.moderator_decision === 'approve_as_non_spoiler'
                                          ? 'Phê duyệt không phải Spoiler'
                                          : feedback.moderator_decision === 'reject_review'
                                            ? 'Từ chối Review'
                                            : feedback.moderator_decision}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-medium">Người xử lý:</span>{' '}
                                    {feedback.moderator?.username || 'N/A'}
                                  </div>
                                </div>
                                {feedback.feedback_type && (
                                  <div className="mb-1">
                                    <span className="font-medium">Loại feedback:</span>{' '}
                                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700">
                                      {feedback.feedback_type === 'correct_spoiler'
                                        ? 'Spoiler chính xác'
                                        : feedback.feedback_type === 'false_positive'
                                          ? 'False positive'
                                          : feedback.feedback_type === 'missed_spoiler'
                                            ? 'Bỏ sót spoiler'
                                            : feedback.feedback_type === 'correct_non_spoiler'
                                              ? 'Không phải spoiler'
                                              : feedback.feedback_type}
                                    </span>
                                  </div>
                                )}
                                {feedback.is_spoiler_correct !== null && (
                                  <div className="mb-1">
                                    <span className="font-medium">Độ chính xác AI:</span>{' '}
                                    <span
                                      className={`rounded px-2 py-1 text-xs ${
                                        feedback.is_spoiler_correct
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-red-100 text-red-700'
                                      }`}
                                    >
                                      {feedback.is_spoiler_correct
                                        ? '✓ Chính xác'
                                        : '✗ Không chính xác'}
                                    </span>
                                  </div>
                                )}
                                {feedback.notes && (
                                  <div className="mb-1">
                                    <span className="font-medium">Ghi chú:</span>{' '}
                                    <span className="italic">"{feedback.notes}"</span>
                                  </div>
                                )}
                                {feedback.created_at && (
                                  <div className="mt-1 text-xs text-green-600">
                                    Xử lý lúc: {formatDate(feedback.created_at)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="mb-3">
                          <p className="mb-1 text-sm text-gray-600">
                            <strong>Phim:</strong> {review.movie?.title || 'N/A'}
                          </p>
                          <div
                            className={`rounded-lg p-3 ${
                              moderationFeedback.length > 0
                                ? 'border-l-4 border-gray-300 bg-gray-50'
                                : 'bg-gray-50'
                            }`}
                          >
                            <p className="text-gray-800">{review.content}</p>
                          </div>
                        </div>

                        {Array.isArray(review.spoiler_detected_patterns) &&
                          review.spoiler_detected_patterns.length > 0 && (
                            <div className="mb-3">
                              <p className="mb-1 text-sm font-medium text-gray-700">
                                Patterns phát hiện:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {review.spoiler_detected_patterns.map((pattern, index) => (
                                  <span
                                    key={index}
                                    className="rounded bg-red-100 px-2 py-1 text-xs text-red-700"
                                  >
                                    {pattern}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col space-y-2">
                        {moderationFeedback.length === 0 ? (
                          <button
                            onClick={() => {
                              setSelectedReview(review);
                              setShowFeedbackModal(true);
                            }}
                            className="rounded bg-blue-600 px-3 py-1 text-sm text-white transition-colors hover:bg-blue-700"
                          >
                            <EyeIcon className="mr-1 inline size-4" />
                            Xử lý
                          </button>
                        ) : (
                          <div className="rounded bg-green-100 px-3 py-1 text-center text-sm text-green-700">
                            <CheckCircleIcon className="mr-1 inline size-4" />
                            Đã hoàn thành
                          </div>
                        )}

                        {/* Additional actions for processed reviews */}
                        {moderationFeedback.length > 0 && (
                          <button
                            onClick={() => {
                              setSelectedReview(review);
                              setShowFeedbackModal(true);
                            }}
                            className="rounded bg-gray-600 px-3 py-1 text-sm text-white transition-colors hover:bg-gray-700"
                          >
                            <EyeIcon className="mr-1 inline size-4" />
                            Xem chi tiết
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="border-t p-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Trang trước
              </button>

              <span className="text-sm text-gray-700">
                Trang {pagination.currentPage} / {pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Trang sau
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && selectedReview && (
        <FeedbackModal
          review={selectedReview}
          onSubmit={handleSubmitFeedback}
          onClose={() => {
            setShowFeedbackModal(false);
            setSelectedReview(null);
          }}
        />
      )}
    </div>
  );
};

// Feedback Modal Component
const FeedbackModal = ({ review, onSubmit, onClose }) => {
  const [feedbackData, setFeedbackData] = useState({
    feedbackType: '',
    moderatorDecision: '',
    isSpoilerCorrect: false,
    difficultyLevel: 'medium',
    notes: '',
    timeSpentSeconds: 0,
  });
  const [startTime] = useState(Date.now());

  // Check if review is already processed
  const moderationFeedback = Array.isArray(review.moderation_feedback)
    ? review.moderation_feedback
    : [];
  const isProcessed = moderationFeedback.length > 0;

  const handleSubmit = e => {
    e.preventDefault();

    // Only allow submission for unprocessed reviews
    if (isProcessed) return;

    // Calculate time spent
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);

    const finalFeedbackData = {
      ...feedbackData,
      timeSpentSeconds: timeSpent,
    };

    onSubmit(review.id, finalFeedbackData);
  };

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-black bg-opacity-50 p-2">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-lg">
        <div className="border-b p-6">
          <h3 className="text-lg font-medium text-black">
            {isProcessed ? 'Chi tiết Review đã xử lý' : 'Feedback cho Review'}
          </h3>
          {isProcessed && (
            <p className="mt-1 text-sm text-green-600">Review này đã được xử lý bởi moderator</p>
          )}
        </div>

        <div className="space-y-4 p-6">
          {/* Review Info */}
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="mb-2 font-medium text-black">Review Content:</p>
            <p className="text-gray-800">{review.content}</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Confidence: {(review.spoiler_confidence * 100).toFixed(1)}%
              </p>
              <p className="text-sm text-gray-600">Phim: {review.movie?.title || 'N/A'}</p>
            </div>
          </div>

          {/* Show processed info if available, otherwise show form */}
          {isProcessed ? (
            <div className="space-y-4">
              <h4 className="border-b pb-2 text-lg font-medium text-green-800">Thông tin xử lý</h4>
              {moderationFeedback.map((feedback, index) => (
                <div key={index} className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-green-700">Quyết định</label>
                      <span
                        className={`inline-block rounded-full px-3 py-1 text-sm ${
                          feedback.moderator_decision === 'approve_as_spoiler'
                            ? 'bg-red-100 text-red-800'
                            : feedback.moderator_decision === 'approve_as_non_spoiler'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {feedback.moderator_decision === 'approve_as_spoiler'
                          ? 'Phê duyệt là Spoiler'
                          : feedback.moderator_decision === 'approve_as_non_spoiler'
                            ? 'Phê duyệt không phải Spoiler'
                            : feedback.moderator_decision === 'reject_review'
                              ? 'Từ chối Review'
                              : feedback.moderator_decision}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-700">
                        Người xử lý
                      </label>
                      <p className="text-gray-800">{feedback.moderator?.username || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-green-700">
                        Loại feedback
                      </label>
                      <span className="inline-block rounded bg-blue-100 px-3 py-1 text-sm text-blue-800">
                        {feedback.feedback_type === 'correct_spoiler'
                          ? 'Spoiler chính xác'
                          : feedback.feedback_type === 'false_positive'
                            ? 'False positive'
                            : feedback.feedback_type === 'missed_spoiler'
                              ? 'Bỏ sót spoiler'
                              : feedback.feedback_type === 'correct_non_spoiler'
                                ? 'Không phải spoiler'
                                : feedback.feedback_type || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-700">
                        Độ chính xác AI
                      </label>
                      <span
                        className={`inline-block rounded px-3 py-1 text-sm ${
                          feedback.is_spoiler_correct
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {feedback.is_spoiler_correct ? '✓ Chính xác' : '✗ Không chính xác'}
                      </span>
                    </div>
                  </div>

                  {feedback.notes && (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-green-700">Ghi chú</label>
                      <p className="rounded border bg-white p-2 italic text-gray-800">
                        "{feedback.notes}"
                      </p>
                    </div>
                  )}

                  {feedback.created_at && (
                    <div className="text-xs text-green-600">
                      Xử lý lúc:{' '}
                      {new Date(feedback.created_at).toLocaleDateString('vi-VN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Feedback Type */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Loại feedback *
                </label>
                <select
                  value={feedbackData.feedbackType}
                  onChange={e => setFeedbackData({ ...feedbackData, feedbackType: e.target.value })}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:ring-blue-500"
                >
                  <option className="text-black" value="">
                    Chọn loại feedback
                  </option>
                  <option className="text-black" value="correct_spoiler">
                    Spoiler chính xác
                  </option>
                  <option className="text-black" value="false_positive">
                    False positive
                  </option>
                  <option className="text-black" value="missed_spoiler">
                    Bỏ sót spoiler
                  </option>
                  <option className="text-black" value="correct_non_spoiler">
                    Không phải spoiler chính xác
                  </option>
                </select>
              </div>

              {/* Moderator Decision */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Quyết định moderator *
                </label>
                <select
                  value={feedbackData.moderatorDecision}
                  onChange={e =>
                    setFeedbackData({ ...feedbackData, moderatorDecision: e.target.value })
                  }
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:ring-blue-500"
                >
                  <option className="text-black" value="">
                    Chọn quyết định
                  </option>
                  <option className="text-black" value="approve_as_spoiler">
                    Phê duyệt là spoiler
                  </option>
                  <option className="text-black" value="approve_as_non_spoiler">
                    Phê duyệt không phải spoiler
                  </option>
                  <option className="text-black" value="reject_review">
                    Từ chối review
                  </option>
                </select>
              </div>

              {/* Is Spoiler Correct */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={feedbackData.isSpoilerCorrect}
                    onChange={e =>
                      setFeedbackData({ ...feedbackData, isSpoilerCorrect: e.target.checked })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Phát hiện spoiler chính xác
                  </span>
                </label>
              </div>

              {/* Difficulty Level */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Mức độ khó</label>
                <select
                  value={feedbackData.difficultyLevel}
                  onChange={e =>
                    setFeedbackData({ ...feedbackData, difficultyLevel: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:ring-blue-500"
                >
                  <option className="text-black" value="easy">
                    Dễ
                  </option>
                  <option className="text-black" value="medium">
                    Trung bình
                  </option>
                  <option className="text-black" value="hard">
                    Khó
                  </option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Ghi chú</label>
                <textarea
                  value={feedbackData.notes}
                  onChange={e => setFeedbackData({ ...feedbackData, notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Ghi chú thêm về quyết định..."
                />
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                >
                  Gửi Feedback
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 rounded-lg bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700"
                >
                  Hủy
                </button>
              </div>
            </form>
          )}

          {/* Close button for processed reviews */}
          {isProcessed && (
            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700"
              >
                Đóng
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoMarkedReviews;
